import { useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import {
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@headerhawk/ui";
import { api, type FeedbackCategory } from "@/lib/api/client";

type SubmitState = "idle" | "submitting" | "success" | "error";

export function FeedbackPopover() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory | "">("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<SubmitState>("idle");

  const canSubmit = category !== "" && message.trim().length > 0;

  const reset = () => {
    setCategory("");
    setMessage("");
    setState("idle");
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      // Reset form after close animation
      setTimeout(reset, 200);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setState("submitting");
    try {
      await api.feedback.submit({
        category: category as FeedbackCategory,
        message: message.trim(),
      });
      setState("success");
      setTimeout(() => {
        setOpen(false);
        setTimeout(reset, 200);
      }, 2000);
    } catch {
      setState("error");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          data-testid="feedback-button"
        >
          <MessageSquarePlus className="h-4 w-4" />
          <span className="hidden sm:inline">Feedback</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        {state === "success" ? (
          <div className="py-4 text-center" data-testid="feedback-success">
            <p className="text-sm font-medium">Thanks for your feedback!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="feedback-category">Category</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as FeedbackCategory)}
              >
                <SelectTrigger
                  id="feedback-category"
                  data-testid="feedback-category"
                >
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="feature">Feature Request</SelectItem>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-message">Message</Label>
              <Textarea
                id="feedback-message"
                data-testid="feedback-message"
                placeholder="What's on your mind?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={4}
              />
            </div>

            {state === "error" && (
              <p
                className="text-sm text-destructive"
                data-testid="feedback-error"
              >
                Failed to send feedback. Please try again.
              </p>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Goes directly to the team
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!canSubmit || state === "submitting"}
                  data-testid="feedback-submit"
                >
                  {state === "submitting" ? "Sending..." : "Send"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </PopoverContent>
    </Popover>
  );
}
